var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Validation-rule administration — list + detail (CZNA-245).
 *
 * Settings screen "Validační pravidla". Three-pane layout: nav + filters,
 * MultiColumnList of imported rules (with source badge + enabled toggle),
 * and a detail/edit pane. Models on `AuthoritiesList.tsx`.
 *
 * Reaching ~360 ALEPH_CONVERTED rows here is the demo goal ("validations are
 * imported"). The detail pane (`ValidationRuleDetail`) handles edit/create.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { useStripes } from '@folio/stripes/core';
import { Paneset, Pane, PaneMenu, Button, MultiColumnList, SearchField, AccordionSet, Accordion, Select, Checkbox, MessageBanner, Callout, Layout, Headline, } from '@folio/stripes/components';
import NavPaneLayout from '../components/NavPaneLayout';
import RuleSourceBadge from '../components/ValidationRuleAdmin/RuleSourceBadge';
import ValidationRuleDetail from '../components/ValidationRuleAdmin/ValidationRuleDetail';
import MarcCheckerModal from '../components/MarcChecker/MarcCheckerModal';
import { useValidationRules, useValidationRuleMeta, useSaveValidationRule } from '../hooks/useValidationRules';
import { getErrorInfo } from '../utils/getErrorCode';
import { getErrorKind } from '../utils/errorKind';
import { TableLoadingSkeleton, TableRefetchOverlay } from '../components/TableStates';
var WRITE_PERM = 'czna-authorities.validation.rules.write';
var PAGE_SIZE = 50;
// Fixed column widths so auto-sizing doesn't blow the checkbox-only "enabled"
// column out to the same width as text columns (CZNA-245 review). `name` is
// now a descriptive Czech sentence so it gets the most room.
export var COLUMN_WIDTHS = {
    enabled: '80px',
    code: '90px',
    name: '340px',
    source: '130px',
    appliesToRecordType: '160px',
    eventTriggers: '150px',
    severity: '120px',
    alephOrigin: '140px',
};
// The validation lifecycle phases in a FIXED display order. The badge number
// is the 1-based position here, so a rule's events always render in the same
// slots regardless of which subset it carries (CZNA-245). Order is the lifecycle,
// so SUBMIT_FOR_REVIEW (CZNA-684) sits where it happens — after the draft edits and
// before publishing — which renumbers the badges after it; the invariant this list
// protects is consistency across RULES, not across releases.
var EVENT_PHASES = [
    'CREATE_DRAFT',
    'UPDATE_DRAFT',
    'SUBMIT_FOR_REVIEW',
    'PUBLISH',
    'MONTHLY_CHECK',
    'IMPORT_JOB',
];
var ValidationRulesList = function () {
    var _a, _b, _c, _d, _f, _g;
    var intl = useIntl();
    var history = useHistory();
    var location = useLocation();
    var stripes = useStripes();
    var canWrite = stripes.hasPerm(WRITE_PERM);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    var calloutRef = React.useRef(null);
    // --- filter state ---
    var _h = useState(''), query = _h[0], setQuery = _h[1];
    var _j = useState(''), appliedQuery = _j[0], setAppliedQuery = _j[1];
    var _k = useState(''), source = _k[0], setSource = _k[1];
    var _l = useState(''), enabled = _l[0], setEnabled = _l[1];
    var _m = useState(''), recordType = _m[0], setRecordType = _m[1];
    var _o = useState(''), event = _o[0], setEvent = _o[1];
    var _p = useState(0), offset = _p[0], setOffset = _p[1];
    var _q = useState('code'), sortBy = _q[0], setSortBy = _q[1];
    var _r = useState('asc'), sortDir = _r[0], setSortDir = _r[1];
    var _s = useState(true), filterPaneVisible = _s[0], setFilterPaneVisible = _s[1];
    var _t = useState(false), checkerOpen = _t[0], setCheckerOpen = _t[1];
    // CZNA-328 — when set, the checker opens scoped to this one rule (single-rule
    // mode); when null the top-button opens it in all-rules mode.
    var _u = useState(null), checkerRule = _u[0], setCheckerRule = _u[1];
    var meta = useValidationRuleMeta().data;
    var params = useMemo(function () { return ({
        query: appliedQuery || undefined,
        source: source || undefined,
        enabled: enabled === '' ? undefined : enabled === 'true',
        recordType: (recordType || undefined),
        event: (event || undefined),
        sortBy: sortBy,
        sortDir: sortDir,
        limit: PAGE_SIZE,
        offset: offset,
    }); }, [appliedQuery, source, enabled, recordType, event, sortBy, sortDir, offset]);
    var _v = useValidationRules(params), data = _v.data, isLoading = _v.isLoading, isFetching = _v.isFetching, isError = _v.isError;
    var rules = (_a = data === null || data === void 0 ? void 0 : data.rules) !== null && _a !== void 0 ? _a : [];
    var totalRecords = (_b = data === null || data === void 0 ? void 0 : data.totalRecords) !== null && _b !== void 0 ? _b : 0;
    // selected rule id / create mode derived from the URL
    var search = new URLSearchParams(location.search);
    var selectedId = (_c = search.get('selected')) !== null && _c !== void 0 ? _c : undefined;
    var isCreating = search.get('create') === '1';
    var openRule = useCallback(function (rule) {
        var sp = new URLSearchParams(location.search);
        sp.delete('create');
        sp.set('selected', rule.id);
        history.push({ search: "?".concat(sp.toString()) });
    }, [history, location.search]);
    var openCreate = useCallback(function () {
        var sp = new URLSearchParams(location.search);
        sp.delete('selected');
        sp.set('create', '1');
        history.push({ search: "?".concat(sp.toString()) });
    }, [history, location.search]);
    var closeDetail = useCallback(function () {
        var sp = new URLSearchParams(location.search);
        sp.delete('selected');
        sp.delete('create');
        history.push({ search: sp.toString() ? "?".concat(sp.toString()) : '' });
    }, [history, location.search]);
    // Inline enabled toggle — PUT with the existing values + flipped enabled.
    var toggleMutation = useSaveValidationRule({
        onSuccess: function () {
            var _a;
            (_a = calloutRef.current) === null || _a === void 0 ? void 0 : _a.sendCallout({
                type: 'success',
                message: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.toggle.success' }),
            });
        },
        onError: function (err) { return __awaiter(void 0, void 0, void 0, function () {
            var info, kind;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, getErrorInfo(err)];
                    case 1:
                        info = _b.sent();
                        kind = getErrorKind(info);
                        (_a = calloutRef.current) === null || _a === void 0 ? void 0 : _a.sendCallout({
                            type: 'error',
                            // CZNA-1625 — no answer in time: the toggle may have been applied.
                            message: kind === 'timeout'
                                ? intl.formatMessage({ id: 'ui-czna-authorities.error.timeout' })
                                : info.status === 409
                                    ? intl.formatMessage({ id: 'ui-czna-authorities.validationRules.save.conflict' })
                                    : intl.formatMessage({ id: 'ui-czna-authorities.validationRules.toggle.error' }),
                        });
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var handleToggle = useCallback(function (rule) {
        toggleMutation.mutate({
            mode: 'update',
            id: rule.id,
            body: {
                code: rule.code,
                name: rule.name,
                expression: rule.expression,
                message: rule.message,
                appliesToRecordType: rule.appliesToRecordType,
                eventTriggers: rule.eventTriggers,
                severityOverrides: rule.severityOverrides,
                documentation: rule.documentation,
                enabled: !rule.enabled,
                expectedUpdatedAt: rule.updatedAt,
            },
        });
    }, [toggleMutation]);
    // Reset paging whenever a filter changes.
    useEffect(function () { setOffset(0); }, [appliedQuery, source, enabled, recordType, event, sortBy, sortDir]);
    var handleSearch = function (e) {
        e.preventDefault();
        setAppliedQuery(query);
    };
    var handleClearSearch = function () {
        setQuery('');
        setAppliedQuery('');
    };
    var handleRowClick = useCallback(function (_e, row) {
        if (!(row === null || row === void 0 ? void 0 : row.id))
            return;
        openRule(row);
    }, [openRule]);
    var handleSort = useCallback(function (_e, meta2) {
        var col = meta2.name;
        if (col !== 'code' && col !== 'name' && col !== 'updatedAt')
            return;
        if (col === sortBy) {
            setSortDir(function (d) { return (d === 'asc' ? 'desc' : 'asc'); });
        }
        else {
            setSortBy(col);
            setSortDir('asc');
        }
    }, [sortBy]);
    var columnMapping = useMemo(function () { return ({
        enabled: React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.column.enabled" }),
        code: React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.column.code" }),
        name: React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.column.name" }),
        source: React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.column.source" }),
        appliesToRecordType: React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.column.recordTypes" }),
        eventTriggers: React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.column.events" }),
        severity: React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.column.severity" }),
        alephOrigin: React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.column.alephOrigin" }),
    }); }, []);
    var formatter = useMemo(function () { return ({
        enabled: function (r) { return (React.createElement(Checkbox, { checked: r.enabled, disabled: !canWrite || toggleMutation.isLoading, "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.validationRules.toggle.aria' }, { code: r.code }), onClick: function (e) { return e.stopPropagation(); }, onChange: function () { return handleToggle(r); } })); },
        code: function (r) { return r.code; },
        name: function (r) { return r.name; },
        source: function (r) { return React.createElement(RuleSourceBadge, { source: r.source }); },
        appliesToRecordType: function (r) { return ((r.appliesToRecordType && r.appliesToRecordType.length > 0)
            ? r.appliesToRecordType.map(function (t) { return intl.formatMessage({ id: "ui-czna-authorities.recordType.".concat(t) }); }).join(', ')
            : intl.formatMessage({ id: 'ui-czna-authorities.validationRules.recordTypes.all' })); },
        eventTriggers: function (r) {
            var _a;
            var present = new Set((_a = r.eventTriggers) !== null && _a !== void 0 ? _a : []);
            return (React.createElement("span", { style: { display: 'inline-flex', gap: '4px', flexWrap: 'wrap' } }, EVENT_PHASES.map(function (ev, idx) {
                if (!present.has(ev))
                    return null;
                var phaseName = intl.formatMessage({ id: "ui-czna-authorities.validationRules.event.".concat(ev) });
                var num = idx + 1;
                return (React.createElement("span", { key: ev, role: "img", title: phaseName, "aria-label": phaseName, "data-testid": "event-badge-".concat(ev), style: {
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '18px',
                        height: '18px',
                        padding: '0 4px',
                        borderRadius: '9px',
                        backgroundColor: '#e0e0e0',
                        color: '#222',
                        fontSize: '11px',
                        fontWeight: 600,
                        lineHeight: '18px',
                    } }, num));
            })));
        },
        severity: function (r) {
            var _a;
            var sevs = Object.values((_a = r.severityOverrides) !== null && _a !== void 0 ? _a : {});
            if (sevs.length === 0)
                return intl.formatMessage({ id: 'ui-czna-authorities.validationRules.severity.default' });
            return Array.from(new Set(sevs)).map(function (s) { return intl.formatMessage({ id: "ui-czna-authorities.validationRules.severity.".concat(s) }); }).join(', ');
        },
        alephOrigin: function (r) { var _a; return (_a = r.alephOrigin) !== null && _a !== void 0 ? _a : ''; },
    }); }, [canWrite, toggleMutation.isLoading, intl, handleToggle]);
    var recordTypeOptions = __spreadArray([
        { value: '', label: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.filter.recordType.all' }) }
    ], ((_d = meta === null || meta === void 0 ? void 0 : meta.recordTypes) !== null && _d !== void 0 ? _d : []).map(function (t) { return ({
        value: t,
        label: intl.formatMessage({ id: "ui-czna-authorities.recordType.".concat(t) }),
    }); }), true);
    var eventOptions = __spreadArray([
        { value: '', label: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.filter.event.all' }) }
    ], ((_f = meta === null || meta === void 0 ? void 0 : meta.events) !== null && _f !== void 0 ? _f : EVENT_PHASES).map(function (ev) { return ({
        value: ev,
        label: intl.formatMessage({ id: "ui-czna-authorities.validationRules.event.".concat(ev) }),
    }); }), true);
    var sourceOptions = __spreadArray([
        { value: '', label: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.filter.source.all' }) }
    ], ((_g = meta === null || meta === void 0 ? void 0 : meta.sources) !== null && _g !== void 0 ? _g : ['ALEPH_CONVERTED', 'MANUAL']).map(function (s) { return ({
        value: s,
        label: intl.formatMessage({ id: "ui-czna-authorities.validationRules.source.".concat(s) }),
    }); }), true);
    var enabledOptions = [
        { value: '', label: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.filter.enabled.all' }) },
        { value: 'true', label: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.enabled.yes' }) },
        { value: 'false', label: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.enabled.no' }) },
    ];
    var filterContent = (React.createElement(AccordionSet, null,
        React.createElement(Accordion, { label: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.filter.source' }), separator: false },
            React.createElement(Select, { value: source, dataOptions: sourceOptions, onChange: function (e) { return setSource(e.target.value); } })),
        React.createElement(Accordion, { label: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.filter.enabled' }), separator: false },
            React.createElement(Select, { value: enabled, dataOptions: enabledOptions, onChange: function (e) { return setEnabled(e.target.value); } })),
        React.createElement(Accordion, { label: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.filter.recordType' }), separator: false },
            React.createElement(Select, { value: recordType, dataOptions: recordTypeOptions, onChange: function (e) { return setRecordType(e.target.value); } })),
        React.createElement(Accordion, { label: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.filter.event' }), separator: false },
            React.createElement(Select, { value: event, dataOptions: eventOptions, onChange: function (e) { return setEvent(e.target.value); } }))));
    var renderContent = function () {
        // CZNA-538 — see AuthoritiesList.
        if ((isLoading || isFetching) && rules.length === 0) {
            return React.createElement(TableLoadingSkeleton, { testId: "validation-rules-loading", columns: [3, 8, 3, 3], rows: 6 });
        }
        if (isError) {
            return (React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.list.loadError" })));
        }
        if (rules.length === 0) {
            return (React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
                React.createElement(Layout, { className: "padding-all-gutter" },
                    React.createElement(Headline, { size: "medium", margin: "none" },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.list.empty" })))));
        }
        return (
        /* CZNA-538 — see AuthoritiesList: the overlay replaces MCL's corner spinner. */
        React.createElement(TableRefetchOverlay, { active: isFetching, testId: "validation-rules-refetching" },
            React.createElement(MultiColumnList, { autosize: true, virtualize: true, contentData: rules, totalCount: totalRecords, pagingType: "prev-next", pagingOffset: offset, pageAmount: PAGE_SIZE, columnMapping: columnMapping, columnWidths: COLUMN_WIDTHS, visibleColumns: ['enabled', 'code', 'name', 'source', 'appliesToRecordType', 'eventTriggers', 'severity', 'alephOrigin'], formatter: formatter, sortedColumn: sortBy, sortDirection: sortDir === 'asc' ? 'ascending' : 'descending', onHeaderClick: handleSort, nonInteractiveHeaders: ['enabled', 'source', 'appliesToRecordType', 'eventTriggers', 'severity', 'alephOrigin'], selectedRow: selectedId ? { id: selectedId } : undefined, onRowClick: handleRowClick, onNeedMoreData: function (_amount, newOffset) { return setOffset(newOffset); } })));
    };
    var detailPane = (isCreating || selectedId) ? (React.createElement(ValidationRuleDetail, { key: isCreating ? 'create' : selectedId, mode: isCreating ? 'create' : 'edit', id: isCreating ? undefined : selectedId, onClose: closeDetail, onSaved: function (saved) {
            var _a;
            (_a = calloutRef.current) === null || _a === void 0 ? void 0 : _a.sendCallout({
                type: 'success',
                message: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.save.success' }),
            });
            openRule(saved);
        }, onDeleted: function () {
            var _a;
            (_a = calloutRef.current) === null || _a === void 0 ? void 0 : _a.sendCallout({
                type: 'success',
                message: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.delete.success' }),
            });
            closeDetail();
        }, onOpenInChecker: function (rule) {
            setCheckerRule(rule);
            setCheckerOpen(true);
        } })) : undefined;
    var firstMenu = (React.createElement(PaneMenu, null,
        React.createElement(Button, { marginBottom0: true, onClick: function () { setCheckerRule(null); setCheckerOpen(true); } },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.marcChecker.action.open" }))));
    var lastMenu = canWrite ? (React.createElement(PaneMenu, null,
        React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, onClick: openCreate },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.action.create" })))) : undefined;
    return (React.createElement(React.Fragment, null,
        React.createElement(Paneset, null,
            filterPaneVisible && (React.createElement(Pane, { id: "rules-filter-pane", defaultWidth: "22%", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.pane.navigation" }), dismissible: true, onClose: function () { return setFilterPaneVisible(false); } },
                React.createElement(NavPaneLayout, null,
                    React.createElement("hr", { style: { border: 'none', borderTop: '1px solid #d9d9d9', margin: '12px 0' } }),
                    React.createElement("form", { onSubmit: handleSearch },
                        React.createElement(AccordionSet, null,
                            React.createElement(Accordion, { label: intl.formatMessage({ id: 'ui-czna-authorities.search' }), separator: false },
                                React.createElement(SearchField, { autoFocus: true, name: "query", value: query, onChange: function (e) { return setQuery(e.target.value); }, onClear: handleClearSearch, placeholder: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.search.placeholder' }) }))),
                        filterContent,
                        React.createElement(Button, { type: "submit", buttonStyle: "primary", marginBottom0: true, fullWidth: true },
                            React.createElement(FormattedMessage, { id: "ui-czna-authorities.search" })))))),
            React.createElement(Pane, { id: "rules-content-pane", defaultWidth: "fill", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.title" }), paneSub: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.resultCount' }, { count: totalRecords }), firstMenu: firstMenu, lastMenu: lastMenu }, renderContent()),
            detailPane),
        React.createElement(Callout, { ref: calloutRef }),
        React.createElement(MarcCheckerModal, { open: checkerOpen, onClose: function () { return setCheckerOpen(false); }, ruleCode: checkerRule === null || checkerRule === void 0 ? void 0 : checkerRule.code, ruleName: checkerRule === null || checkerRule === void 0 ? void 0 : checkerRule.name, ruleId: checkerRule === null || checkerRule === void 0 ? void 0 : checkerRule.id })));
};
export default ValidationRulesList;
